import { useContext, useEffect, useState } from "react";
import { toast } from '../../../utils/toast';
import CategoryIcon from "@mui/icons-material/Category";
import {
  useArticulosApi,
  ArticuloItem,
} from "../../../services/api/articulosApi";
import ArticulosModal from "../../../components/module/almacen/ArticulosModal/ArticulosModal";
import ArticulosDataTable from "../../../components/module/almacen/ArticulosDataTable/ArticulosDataTable";
import PageLayout from "../../../components/layout/PageLayout";
import Swal from "sweetalert2";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  IconButton,
  Switch,
  Tooltip,
  Chip,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon } from "@mui/icons-material";
import ArticuloDetailsModal from "./ArticuloDetailsModal";
import ArticulosImportModal from "../../../components/module/almacen/ArticulosImportModal/ArticulosImportModal";
import { DarkModeContext } from "../../../context/darkModeContext";

const Articulos = () => {
  const [items, setItems] = useState<ArticuloItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ArticuloItem | null>(null);
  const { isLoading, getAll, create, update, remove } = useArticulosApi();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedArticulo, setSelectedArticulo] = useState<ArticuloItem | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState("");
  const { darkMode } = useContext(DarkModeContext);

  const loadItems = async () => {
    try {
      const data = await getAll(page, pageSize, search || undefined);
      setItems(data.items);
      setTotal(data.total);
    } catch (error) {
      console.error('Error cargando items:', error);
      toast.error("No se pudieron cargar los artículos.");
    }
  };

  useEffect(() => {
    loadItems();
  }, [page, pageSize, search]);

  const handleSearch = (term: string) => {
    setSearch(term);
    setPage(1); // reset to first page on new search
  };

  const handlePageChange = (newPage: number) => setPage(newPage);
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const columns: GridColDef[] = [
    { field: "Id", headerName: "ID", width: 60 },
    { field: "EAN", headerName: "Código EAN", width: 130 },
    { field: "Nombre", headerName: "Nombre", flex: 1, minWidth: 180 },
    {
      field: "categoria",
      headerName: "Categoría",
      width: 140,
      valueGetter: (params) => params.row.categoria?.Nombre || "—",
    },
    {
      field: "PrecioVenta",
      headerName: "P. Venta",
      width: 110,
      type: "number",
      valueFormatter: (params) =>
        typeof params.value === "number"
          ? `$ ${params.value.toLocaleString("es-CO")}`
          : "—",
    },
    {
      field: "margen",
      headerName: "Margen",
      width: 100,
      sortable: true,
      valueGetter: (params) => {
        const compra = Number(params.row.PrecioCompra);
        const venta = Number(params.row.PrecioVenta);
        if (!compra || compra === 0) return null;
        return Math.round(((venta - compra) / compra) * 100);
      },
      renderCell: (params: GridRenderCellParams) => {
        const margen = params.value as number | null;
        if (margen === null) return "—";
        const color = margen < 0 ? "#ef5350" : margen < 15 ? "#ff9800" : "#4caf50";
        return (
          <Chip
            label={`${margen}%`}
            size="small"
            sx={{
              bgcolor: `${color}18`,
              color,
              fontWeight: 700,
              fontSize: "0.75rem",
              height: 22,
              border: `1px solid ${color}40`,
            }}
          />
        );
      },
    },
    {
      field: "Activo",
      headerName: "Estado",
      width: 90,
      renderCell: (params: GridRenderCellParams) => (
        <Switch
          checked={Boolean(params.row.Activo)}
          onChange={(e) => handleStatusChange(params.row.Id, e.target.checked)}
          color="primary"
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 130,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="Ver detalle">
            <IconButton onClick={() => handleView(params.row)} color="info" size="small">
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton onClick={() => handleEdit(params.row)} color="primary" size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton onClick={() => handleDelete(params.row.Id)} color="error" size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const handleView = (articulo: ArticuloItem) => {
    setSelectedArticulo(articulo);
    setIsDetailsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: ArticuloItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    const result = await Swal.fire({
      title: "¿Eliminar artículo?",
      text: "El artículo quedará deshabilitado y no aparecerá en el sistema.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef5350",
      cancelButtonColor: "#9e9e9e",
    });

    if (result.isConfirmed) {
      try {
        await remove(id);
        await loadItems();
        toast.success("El artículo ha sido eliminado.");
      } catch (error) {
        console.error("Error deleting item:", error);
        toast.error("No se pudo eliminar el artículo.");
      }
    }
  };

  const handleStatusChange = async (id: number, active: boolean) => {
    const result = await Swal.fire({
      title: active ? "¿Activar artículo?" : "¿Desactivar artículo?",
      text: active
        ? "El artículo estará disponible para ventas y búsquedas."
        : "El artículo no aparecerá en búsquedas ni en ventas.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: active ? "Sí, activar" : "Sí, desactivar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: active ? "#4caf50" : "#ff9800",
      cancelButtonColor: "#9e9e9e",
    });

    if (result.isConfirmed) {
      // Optimistic update — avoids waiting for reload
      setItems(prev => prev.map(i => i.Id === id ? { ...i, Activo: active } : i));
      try {
        const item = items.find((i) => i.Id === id);
        if (item) {
          await update(id, {
            Activo: active,
            IdCategoria: item.IdCategoria,
            IdEmbalaje: item.IdEmbalaje,
            IdImpuesto: item.IdImpuesto,
            Nombre: item.Nombre,
            Descripcion: item.Descripcion,
            FechaAlta: item.FechaAlta,
            EAN: item.EAN,
            UnidadesPorEmbalaje: item.UnidadesPorEmbalaje,
            Referencia: item.Referencia,
            PrecioCompra: item.PrecioCompra,
            PrecioVenta: item.PrecioVenta,
            Descuento: item.Descuento,
          });
        }
      } catch (error) {
        // Revert optimistic update on failure
        setItems(prev => prev.map(i => i.Id === id ? { ...i, Activo: !active } : i));
        console.error("Error updating status:", error);
        toast.error("No se pudo actualizar el estado.");
      }
    }
  };

  return (
    <PageLayout icon={CategoryIcon} title="Artículos">
        <Box sx={{ padding: "24px", paddingBottom: "48px", minHeight: "calc(100vh - 200px)" }}>
          <ArticulosDataTable
            items={items}
            columns={columns}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            onAdd={handleAdd}
            onImport={() => setIsImportModalOpen(true)}
            isLoading={isLoading}
            page={page}
            pageSize={pageSize}
            totalRows={total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onSearch={handleSearch}
            searchValue={search}
          />

          <ArticulosModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={async (data) => {
              setIsSaving(true);
              try {
                if (editingItem) {
                  await update(editingItem.Id, data);
                } else {
                  await create(data);
                }
                setIsModalOpen(false);
                await loadItems();
                Swal.fire("Éxito", editingItem ? "Artículo actualizado." : "Artículo creado.", "success");
              } catch (error) {
                console.error("Error saving item:", error);
                toast.error("No se pudo guardar el artículo.");
              } finally {
                setIsSaving(false);
              }
            }}
            title={editingItem ? "Editar Artículo" : "Nuevo Artículo"}
            initialData={
              editingItem
                ? {
                  IdCategoria: editingItem.IdCategoria,
                  IdEmbalaje: editingItem.IdEmbalaje,
                  IdImpuesto: editingItem.IdImpuesto,
                  Nombre: editingItem.Nombre,
                  Descripcion: editingItem.Descripcion,
                  FechaAlta: editingItem.FechaAlta,
                  EAN: editingItem.EAN,
                  UnidadesPorEmbalaje: editingItem.UnidadesPorEmbalaje,
                  Referencia: editingItem.Referencia,
                  PrecioCompra: editingItem.PrecioCompra,
                  PrecioVenta: editingItem.PrecioVenta,
                  Descuento: editingItem.Descuento,
                  Activo: editingItem.Activo,
                }
                : undefined
            }
            isLoading={isSaving}
            isEdit={!!editingItem}
          />

          <ArticuloDetailsModal
            open={isDetailsModalOpen}
            onClose={() => setIsDetailsModalOpen(false)}
            articulo={selectedArticulo}
          />

          <ArticulosImportModal
            isOpen={isImportModalOpen}
            onClose={() => setIsImportModalOpen(false)}
            onImportComplete={loadItems}
          />
        </Box>
      </PageLayout>
  );
};

export default Articulos;
